import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, Calendar as CalendarIcon, Trash2, Pencil, Code } from "lucide-react";
import { Avatar, Badge, Button, ColorSwatchPicker, Combobox, EmptyState, Input, Label, Modal, ModalFooter, Skeleton, Textarea, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { TIMEZONES } from "@/lib/timezones";
import type { Calendar } from "@/lib/types";

/**
 * Public booking-link management. Each "scheduling link" is a Calendar
 * with a URL slug. The user shares /book/<slug> with customers and they
 * can self-book a time. This page is the management view.
 */
export function SchedulingLinksPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Calendar | "new" | null>(null);
  const [embedFor, setEmbedFor] = useState<Calendar | null>(null);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ calendars: Calendar[] }>("/calendars");
      setCalendars(d.calendars);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (c: Calendar) => {
    if (!(await confirm({
      title: `Delete "${c.name}"?`,
      description: "Existing appointments will be detached, not deleted. The booking URL will stop working.",
      confirmLabel: "Delete link",
      danger: true,
    }))) return;
    setCalendars((prev) => prev.filter((x) => x.id !== c.id));
    try {
      await api.delete(`/calendars/${c.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      load();
    }
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`${baseUrl}/book/${slug}`);
    toast.success("Copied");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Booking links</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Public links your customers use to book time on your calendar. Share the URL or embed it on a page.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>
          New booking link
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : calendars.length === 0 ? (
        <EmptyState
          icon={CalendarIcon}
          title="No booking links yet"
          description="Each booking link is a public page (like Calendly) where customers self-book a time. They show up on your Calendar automatically."
          action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Create your first booking link</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {calendars.map((c) => {
            const url = `${baseUrl}/book/${c.slug}`;
            return (
              <div key={c.id} className="group flex flex-col rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4 transition-colors hover:border-zinc-700">
                <div className="flex items-start justify-between gap-2">
                  <Badge color={c.color}>{c.name}</Badge>
                  <span className="text-xs text-zinc-500">{c.duration_minutes} min</span>
                </div>
                {c.description && <p className="mt-2 text-xs text-zinc-400 line-clamp-2">{c.description}</p>}
                <p className="mt-3 truncate font-mono text-xs text-zinc-500">{url}</p>
                <p className="mt-1 text-[10px] text-zinc-600">{c.timezone}</p>
                <div className="mt-3 flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                  <Button variant="outline" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" />} onClick={() => copyUrl(c.slug)} className="flex-1">
                    Copy link
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => window.open(url, "_blank")} aria-label="Open"><ExternalLink className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setEmbedFor(c)} aria-label="Embed code"><Code className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setEditing(c)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(c)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CalendarEditDialog
          calendar={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {embedFor && (
        <EmbedCodeDialog
          calendar={embedFor}
          baseUrl={baseUrl}
          onClose={() => setEmbedFor(null)}
        />
      )}
    </div>
  );
}

function CalendarEditDialog({
  calendar, onClose, onSaved,
}: { calendar: Calendar | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(calendar?.name ?? "");
  const [slug, setSlug] = useState(calendar?.slug ?? "");
  const [description, setDescription] = useState(calendar?.description ?? "");
  const [duration, setDuration] = useState(calendar?.duration_minutes ?? 30);
  const [tz, setTz] = useState(calendar?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [color, setColor] = useState(calendar?.color ?? "indigo");
  const [bufferBefore, setBufferBefore] = useState(calendar?.buffer_before_minutes ?? 0);
  const [bufferAfter, setBufferAfter] = useState(calendar?.buffer_after_minutes ?? 0);
  const [minNotice, setMinNotice] = useState(calendar?.min_notice_minutes ?? 60);
  const [maxAdvance, setMaxAdvance] = useState(calendar?.max_advance_days ?? 60);
  const [saving, setSaving] = useState(false);

  // Auto-generate slug from name when creating.
  useEffect(() => {
    if (!calendar) {
      setSlug(name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }, [name, calendar]);

  const submit = async () => {
    if (!name.trim() || !slug) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(), slug,
        description: description.trim() || null,
        duration_minutes: duration,
        timezone: tz,
        color,
        buffer_before_minutes: bufferBefore,
        buffer_after_minutes: bufferAfter,
        min_notice_minutes: minNotice,
        max_advance_days: maxAdvance,
      };
      if (calendar) await api.patch(`/calendars/${calendar.id}`, body);
      else          await api.post("/calendars", body);
      toast.success(calendar ? "Saved" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={calendar ? "Edit booking link" : "New booking link"} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label required>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 30-min Demo" autoFocus /></div>
          <div>
            <Label required>URL slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="demo-call" />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown to customers on the booking page" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Duration (min)</Label><Input type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10) || 30)} /></div>
          <div>
            <Label>Timezone</Label>
            <Combobox
              value={tz}
              onChange={setTz}
              options={(TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES]).map((z) => ({ value: z }))}
              placeholder="America/Los_Angeles"
            />
          </div>
          <div><Label>Buffer before (min)</Label><Input type="number" value={bufferBefore} onChange={(e) => setBufferBefore(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Buffer after (min)</Label><Input type="number" value={bufferAfter} onChange={(e) => setBufferAfter(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Min notice (min)</Label><Input type="number" value={minNotice} onChange={(e) => setMinNotice(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Max advance (days)</Label><Input type="number" value={maxAdvance} onChange={(e) => setMaxAdvance(parseInt(e.target.value, 10) || 60)} /></div>
        </div>
        <div>
          <Label>Color</Label>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !slug}>{calendar ? "Save changes" : "Create"}</Button>
      </ModalFooter>
    </Modal>
  );
}

function EmbedCodeDialog({ calendar, baseUrl, onClose }: { calendar: Calendar; baseUrl: string; onClose: () => void }) {
  const toast = useToast();
  const url = `${baseUrl}/book/${calendar.slug}`;
  const iframe = `<iframe src="${url}" width="100%" height="700" frameborder="0" style="border-radius: 12px;"></iframe>`;
  const link = `<a href="${url}" target="_blank">Book a time with us</a>`;

  return (
    <Modal open onClose={onClose} title={`Embed "${calendar.name}"`} size="lg">
      <p className="text-sm text-zinc-400 mb-3">Drop this code into any web page to let visitors book directly.</p>
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Iframe (recommended)</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(iframe); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">{iframe}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Simple link</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">{link}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Direct URL</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto">{url}</pre>
        </div>
      </div>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}
